import React, { useState, useEffect } from 'react';
import { MathJax, MathJaxContext } from 'better-react-mathjax';
import MathTextRenderer from './MathTextRenderer';

// Helper function for formatting custom tags into LaTeX
const formatCustomTagsToLaTeX = (text) => {
  if (!text) return '';
  
  // Handle chemical formulas within <chem> tags
  text = text.replace(/<chem>([^<]+)<\/chem>/g, '\\ce{$1}');
  
  // Handle fractions (fraq{} to \\frac{})
  text = text.replace(/fraq{([^}]+)}{([^}]+)}/g, '\\frac{$1}{$2}');
  
  // Handle simple powers (e.g., x^2 -> x^{2})
  text = text.replace(/\^(\d+)/g, '^{$1}');
  
  // Handle simple subscripts (e.g., x_2 -> x_{2})
  text = text.replace(/_(\d+)/g, '_{$1}');

  // Removed the complex regex for auto-wrapping standard LaTeX commands
  // Relying on MathJaxContext configuration and the MathJax component to handle delimiters like \( \) and \[ \]

  return text;
};

const QuestionFormatter = ({ value, onChange, preview = false, showGuide = false }) => {
  const [formattedValue, setFormattedValue] = useState(value || '');

  // Configuration for MathJax (used for context and now for preview rendering)
  const config = {
    loader: { load: ["[tex]/html"] },
    tex: {
      packages: { "[+]": ["html"] },
      inlineMath: [["$", "$"], ["\\(", "\\)"]], // Add \( \) as inline delimiters
      displayMath: [["$$", "$$"], ["\\[", "\\]"]], // Add \[ \] as display delimiters
      processEscapes: true,
      macros: { // Add support for \ce{} macro
        '\\ce': '{\\text{#1}}'
      }
    },
     options: { // Optional: Configure MathJax options if needed
      enableMenu: false, // Disable right-click MathJax menu
    },
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    setFormattedValue(newValue);
    // Call onChange only with the raw value, as formatting is for display/preview
    onChange(newValue);
  };

  // Effect to update formattedValue when the external value prop changes
  useEffect(() => {
    setFormattedValue(value || '');
  }, [value]);

  return (
    <MathJaxContext config={config}>
      <div className="space-y-2">
        <textarea
          value={formattedValue}
          onChange={handleChange}
          className="w-full p-2 border rounded-md"
          rows={4}
          placeholder="Enter your question with formatting tags and LaTeX..."
        />
        
        {preview && (
          <div className="mt-4 p-4 bg-gray-50 rounded-md">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Preview:</h4>
            <div className="mathjax-preview">
              <MathTextRenderer text={formatCustomTagsToLaTeX(formattedValue)} />
            </div>
          </div>
        )}

        {showGuide && (
          <div className="text-sm text-gray-500">
            <p>Formatting Guide:</p>
            <ul className="list-disc list-inside">
              <li>Fractions (custom): fraq&#123;num&#125;&#123;den&#125;</li>
              <li>Square roots (LaTeX): \\sqrt&#123;expr&#125;</li>
              <li>Powers (simple): x^2</li>
              <li>Powers (LaTeX): x^&#123;expr&#125;</li>
              <li>Subscripts (simple): x_2</li>
              <li>Subscripts (LaTeX): x_&#123;expr&#125;</li>
              <li>Chemical formulas: &lt;chem&gt;Formula&lt;/chem&gt;</li>
              <li>Inline math (LaTeX): $expression$ or \\(expression\\)</li>
              <li>Display math (LaTeX): $$expression$$ or \\[expression\\]</li>
             {/* Removed the confusing note about auto-wrapping */}
            </ul>
          </div>
        )}
      </div>
    </MathJaxContext>
  );
};

export default QuestionFormatter;
export { formatCustomTagsToLaTeX }; 